import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { usePlayer } from "../contexts/PlayerContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import {
  Play,
  Pause,
  Trash2,
  Music,
  Headphones,
  ListMusic,
  Volume2,
  SkipBack,
  SkipForward,
  Shuffle,
  Repeat,
  X,
  Loader2,
  Zap,
  Moon,
  Flame,
  Coffee,
  Target,
  Share2,
  Globe,
  Users
} from "lucide-react";
import { toast } from "sonner";

const vibeIconMap = {
  Zap,
  Moon,
  Flame,
  Coffee,
  Target,
  Music
};

export default function PlaylistPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { playTrack, playingItem, isPlaying, closePlayer } = usePlayer();
  const [playlist, setPlaylist] = useState({ items: [], total: 0 });
  const [vibes, setVibes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shuffle, setShuffle] = useState(false);
  const [isShared, setIsShared] = useState(false);
  const [shareDescription, setShareDescription] = useState("");
  const [savingShare, setSavingShare] = useState(false);

  useEffect(() => {
    if (user) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [playlistRes, vibesRes, sharingRes] = await Promise.all([
        axios.get(`${API}/playlist`),
        axios.get(`${API}/vibes`),
        axios.get(`${API}/playlist/sharing-status`)
      ]);
      setPlaylist(playlistRes.data);
      setVibes(vibesRes.data.vibes || []);
      setIsShared(sharingRes.data.is_shared || false);
      setShareDescription(sharingRes.data.share_description || "");
    } catch (error) {
      console.error("Failed to fetch playlist", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleShare = async () => {
    if (playlist.total === 0) {
      toast.error("Add songs to your playlist before sharing");
      return;
    }

    setSavingShare(true);
    try {
      const newSharedState = !isShared;
      await axios.post(`${API}/playlist/share`, {
        is_shared: newSharedState,
        description: shareDescription
      });
      
      setIsShared(newSharedState);
      toast.success(newSharedState ? "Playlist shared with community!" : "Playlist is now private");
    } catch (error) {
      toast.error("Failed to update sharing status");
    } finally {
      setSavingShare(false);
    }
  };

  const handleSaveDescription = async () => {
    if (!isShared) return;
    
    setSavingShare(true);
    try {
      await axios.post(`${API}/playlist/share`, {
        is_shared: true,
        description: shareDescription
      });
      toast.success("Description updated!");
    } catch (error) {
      toast.error("Failed to update description");
    } finally {
      setSavingShare(false);
    }
  };

  const getVibeInfo = (vibeId) => vibes.find(v => v.id === vibeId);

  const handlePlay = async (item, index) => {
    // If same item, toggle pause/play via context
    if (playingItem?.content_id === item.content_id) {
      // Context handles toggling internally when same track
      await playTrack(item, { queue: playlist.items, queueIndex: index });
      return;
    }
    try {
      // Build queue (optionally shuffled) so next/prev work globally
      let queueOrder = playlist.items;
      let resolvedIndex = index;
      if (shuffle) {
        queueOrder = [...playlist.items].sort(() => Math.random() - 0.5);
        resolvedIndex = queueOrder.findIndex(q => q.content_id === item.content_id);
      }
      await playTrack(item, { queue: queueOrder, queueIndex: resolvedIndex });
    } catch (e) {
      // already toasted by context
    }
  };

  const handleRemove = async (contentId) => {
    try {
      await axios.delete(`${API}/playlist/remove/${contentId}`);
      setPlaylist(prev => ({
        ...prev,
        items: prev.items.filter(i => i.content_id !== contentId),
        total: prev.total - 1
      }));
      
      if (playingItem?.content_id === contentId) {
        closePlayer();
      }
      
      toast.success("Removed from playlist");
    } catch (error) {
      toast.error("Failed to remove from playlist");
    }
  };

  const handleClearPlaylist = async () => {
    if (!confirm("Are you sure you want to clear your entire playlist?")) return;
    
    try {
      await axios.delete(`${API}/playlist/clear`);
      setPlaylist({ items: [], total: 0 });
      closePlayer();
      toast.success("Playlist cleared");
    } catch (error) {
      toast.error("Failed to clear playlist");
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!user) {
    return (
      <div className="min-h-screen py-12">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <ListMusic className="h-16 w-16 text-gold mx-auto mb-4" />
          <h1 className="font-serif text-3xl font-bold mb-4">My Playlist</h1>
          <p className="text-muted-foreground mb-6">Sign in to create and manage your playlist</p>
          <Button onClick={() => navigate("/login")} className="bg-gold text-primary-foreground hover:bg-gold/90">
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="playlist-page">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold flex items-center gap-3">
              <ListMusic className="h-8 w-8 text-gold" />
              My Playlist
            </h1>
            <p className="text-muted-foreground mt-1">
              {playlist.total} {playlist.total === 1 ? 'song' : 'songs'}
            </p>
          </div>
          
          {playlist.total > 0 && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePlay(playlist.items[0], 0)}
                className="border-gold text-gold hover:bg-gold hover:text-primary-foreground"
                data-testid="play-all-btn"
              >
                <Play className="h-4 w-4 mr-1" />
                Play All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearPlaylist}
                className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                data-testid="clear-playlist-btn"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
          )}
        </div>

        {/* Playlist Content */}
        {playlist.total === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <ListMusic className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="font-serif text-xl font-semibold mb-2">Your playlist is empty</h2>
              <p className="text-muted-foreground mb-6">
                Add songs from the library to start building your playlist
              </p>
              <Button onClick={() => navigate("/library")} className="bg-gold text-primary-foreground hover:bg-gold/90">
                Browse Library
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Sharing Card */}
            <Card className="mb-6" data-testid="sharing-card">
              <CardContent className="py-4">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex items-center gap-3 flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isShared ? 'bg-gold/20' : 'bg-muted'}`}>
                      {isShared ? <Globe className="h-5 w-5 text-gold" /> : <Share2 className="h-5 w-5 text-muted-foreground" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="font-medium">Share with Community</span>
                        <Switch
                          checked={isShared}
                          onCheckedChange={handleToggleShare}
                          disabled={savingShare}
                          data-testid="share-toggle"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {isShared ? "Your playlist is visible to all members" : "Your playlist is private"}
                      </p>
                    </div>
                  </div>
                  
                  {isShared && (
                    <div className="flex gap-2 items-center flex-1">
                      <Input
                        placeholder="Add a description (e.g., 'My Contracts study mix')"
                        value={shareDescription}
                        onChange={(e) => setShareDescription(e.target.value)}
                        className="flex-1"
                        data-testid="share-description-input"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleSaveDescription}
                        disabled={savingShare}
                      >
                        {savingShare ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                      </Button>
                    </div>
                  )}
                </div>
                
                {isShared && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <Button
                      variant="link"
                      size="sm"
                      className="text-gold p-0 h-auto"
                      onClick={() => navigate("/discover")}
                    >
                      <Users className="h-4 w-4 mr-1" />
                      Discover other members' playlists
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Songs List */}
            <Card>
              <CardContent className="p-0">
                <div className="divide-y divide-border">
                  {playlist.items.map((item, index) => {
                    const vibe = getVibeInfo(item.vibe);
                    const VibeIcon = vibe ? vibeIconMap[vibe.icon] : Music;
                    const isCurrentlyPlaying = playingItem?.content_id === item.content_id;

                    return (
                      <div
                        key={item.content_id}
                        className={`flex items-center gap-4 p-4 hover:bg-muted/30 transition-colors ${
                          isCurrentlyPlaying ? 'bg-gold/10' : ''
                        }`}
                        data-testid={`playlist-item-${item.content_id}`}
                      >
                        {/* Index / Play indicator */}
                        <div className="w-8 text-center">
                          {isCurrentlyPlaying && isPlaying ? (
                            <Volume2 className="h-4 w-4 text-gold mx-auto animate-pulse" />
                          ) : (
                            <span className="text-sm text-muted-foreground">{index + 1}</span>
                        )}
                      </div>

                      {/* Vibe Icon */}
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${vibe?.color || '#c9a227'}20` }}
                      >
                        <VibeIcon className="h-5 w-5" style={{ color: vibe?.color || '#c9a227' }} />
                      </div>

                      {/* Song Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className={`font-medium truncate ${isCurrentlyPlaying ? 'text-gold' : ''}`}>
                          {item.title}
                        </h3>
                        {item.song_title && (
                          <p className="text-sm font-medium truncate text-foreground/80">
                            {item.song_title}
                          </p>
                        )}
                        <p className="text-sm text-muted-foreground truncate">
                          {item.course_name} • {vibe?.name || item.vibe}
                        </p>
                      </div>

                      {/* Duration */}
                      <span className="text-sm text-muted-foreground">
                        {formatDuration(item.duration)}
                      </span>

                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant={isCurrentlyPlaying ? "default" : "ghost"}
                          className={isCurrentlyPlaying ? "bg-gold text-primary-foreground" : ""}
                          onClick={() => handlePlay(item, index)}
                        >
                          {isCurrentlyPlaying && isPlaying ? (
                            <Pause className="h-4 w-4" />
                          ) : (
                            <Play className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => handleRemove(item.content_id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          </>
        )}
      </div>

    </div>
  );
}
